import {
    getAriaControlsElement,
} from "./base";
import {
    Spring,
    SpringParameters,
} from "../spring";

export function isCarouselButton(element: HTMLElement): element is HTMLButtonElement {
    if (element instanceof HTMLButtonElement &&
        element.hasAttribute("aria-controls") &&
        element.hasAttribute("data-carousel")) {
        return true;
    } else {
        return false;
    }
}

export function handleCarouselButton(button: HTMLButtonElement) {
    const carousel = button.dataset.carousel;
    if (carousel === "previous" || carousel === "prev") {
        handlePrevious(button);
    } else if (carousel === "next") {
        handleNext(button);
    } else if (carousel === "bullet") {
        handleBullet(button);
    }
}

function getPanel(target: EventTarget | null): HTMLElement | undefined {
    if (!(target instanceof Element)) {
        return undefined;
    }

    const element = target.closest("[data-carousel]");
    if (element instanceof HTMLElement && element.getAttribute("data-carousel") === "container") {
        return element;
    } else {
        return undefined;
    }
}

export function handleCarouselPanelMouseDown(event: MouseEvent) {
    const panel = getPanel(event.target);
    if (!panel) {
        return false;
    }

    return false;
}

export function handleCarouselPanelMouseMove(event: MouseEvent) {
    const panel = getPanel(event.target);
    if (!panel) {
        return false;
    }

    return false;
}

export function handleCarouselPanelMouseUp(event: MouseEvent) {
    const panel = getPanel(event.target);
    if (!panel) {
        return false;
    }

    return false;
}

export function handleCarouselPanelMouseLeave(event: MouseEvent) {
    const panel = getPanel(event.target);
    if (!panel) {
        return false;
    }

    return false;
}

export function handleCarouselPanelMouseOut(event: MouseEvent) {
    const panel = getPanel(event.target);
    if (!panel) {
        return false;
    }

    return false;
}

function getContainer(button: HTMLButtonElement) {
    const controls = getAriaControlsElement(button);
    if (typeof controls === "undefined") {
        return undefined;
    }

    const container = controls.closest("[data-carousel=container]");
    if (!(container instanceof HTMLElement)) {
        return undefined;
    }

    return container;
}

function getCurrentIndex(container: HTMLElement) {
    const index = Number.parseInt(container.dataset.current, 10);
    if (typeof index === "number" && Number.isSafeInteger(index)) {
        return index;
    } else {
        return 0;
    }
}

function handlePrevious(button: HTMLButtonElement) {
    const container = getContainer(button);
    if (typeof container === "undefined") {
        return;
    }
    const currentIndex = getCurrentIndex(container);
    if (currentIndex === 0) {
        return;
    }

    const targetIndex = ((currentIndex - 1) + container.children.length) % container.children.length;
    change(container, targetIndex);
}

function handleNext(button: HTMLButtonElement) {
    const container = getContainer(button);
    if (typeof container === "undefined") {
        return;
    }
    const currentIndex = getCurrentIndex(container);
    if (currentIndex >= container.children.length - 1) {
        return;
    }

    const targetIndex = ((currentIndex + 1) + container.children.length) % container.children.length;
    change(container, targetIndex);
}

function handleBullet(button: HTMLButtonElement) {
    const container = getContainer(button);
    if (typeof container === "undefined") {
        return;
    }

    change(container, Number.parseInt(button.dataset.index, 10));
}

let animationId: number | undefined = undefined;
let previousTimestamp: number | undefined = undefined;
let currentContainer: HTMLElement | undefined = undefined;
let currentPosition = 0;
let velocity = 0;
const spring = new SpringParameters(1.0, 0.7);

function change(container: HTMLElement, index: number) {
    container.dataset.current = index.toString(10);

    for (const bullet of [...document.querySelectorAll("[data-carousel=bullet]")]) {
        if (!(bullet instanceof HTMLElement)) {
            continue;
        }
        if (bullet.dataset.index == index.toString(10)) {
            bullet.style.width = "32px";
        } else {
            bullet.style.width = "16px";
        }
    }

    if (typeof animationId === "undefined") {
        currentContainer = container;
        animationId = requestAnimationFrame(update);
    }
}

function update(timestamp: DOMHighResTimeStamp) {
    if (typeof previousTimestamp === "undefined") {
        previousTimestamp = timestamp;
        animationId = requestAnimationFrame(update);
        return;
    }

    const deltaTimeInMilliseconds = (timestamp - previousTimestamp);
    const deltaTime = deltaTimeInMilliseconds / 1000;

    const targetIndex = getCurrentIndex(currentContainer!);
    const targetPosition = -(targetIndex * 100);

    updateSpring(deltaTime, targetPosition);

    const children = currentContainer!.children;
    for (let i = 0; i < children.length; i++) {
        const child = children.item(i);
        if (!(child instanceof HTMLElement)) {
            continue;
        }

        const childPosition = currentPosition + (i * 100);
        child.style.left = `${childPosition}%`;
        child.style.visibility = (-100 < childPosition && childPosition < 100) ? "visible" : "hidden";
    }

    previousTimestamp = timestamp;

    if (currentPosition === targetPosition && velocity === 0) {
        animationId = undefined;
        previousTimestamp = undefined;
    } else {
        animationId = requestAnimationFrame(update);
    }
}

function updateSpring(deltaTime: number, targetPosition: number) {
    const fixedDeltaTime = 1 / 60;
    for (deltaTime = Math.min(deltaTime, 1.0); deltaTime > 0; deltaTime -= fixedDeltaTime) {
        const dt = Math.min(deltaTime, fixedDeltaTime);
        velocity += spring.calculateForce(currentPosition, targetPosition, velocity) * dt;
        velocity = Math.min(Math.max(velocity, -300000 * 100), +300000 * 100);
        currentPosition += velocity * dt;

        const dx = currentPosition - targetPosition;
        if (isReached(dx * dx, velocity * velocity, dt)) {
            currentPosition = targetPosition;
            velocity = 0;
        }
    }
}

function isReached(distanceSquare: number, speedSquare: number, dt: number)
{
    const threshold = 0.01;
    const speedThreshold = (threshold / dt) * 0.25;
    return distanceSquare < threshold * threshold && speedSquare < speedThreshold * speedThreshold;
}
